import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Phone } from "lucide-react";
import { contactPolicy, type ContactPolicyContact } from "@shared/contactPolicy";
import { callLog, emailSend, smsSend } from "@shared/schemas/messages";
import { evaluation, parseInput } from "@shared/validation/issues";
import { ApiError, errorMessage } from "@web/lib/api";
import { fmtPhone } from "@web/lib/format";
import { warningMessage } from "@web/lib/formatLeads";
import { useFormValidation, validationUnavailable } from "@web/lib/formValidation";
import { manualMessageFindings, manualSendFeedback, uncertainSendMessage } from "@web/lib/manualMessage";
import { leadApi, quickRepliesQuery } from "@web/lib/queries-leads";
import { BottomSheet, Button, ChipStrip, SegmentedControl, SelectChip, Skeleton, Stack, Text, TextArea, TextField, UnsavedChanges, ValidationSummary, useToast } from "@web/ui";

/** Explicit selected contact and consent state; never infer the primary recipient in a sheet. */
export type CommsTarget = ContactPolicyContact & { leadId: string; contactId: string; name: string };

/** Maps the SMS 422 (`SMS_CLIENT_ENABLED=0`) to the toast the team expects. */
export const smsError = (e: unknown) => (e instanceof ApiError && e.status === 422 && e.message === "sms_client_disabled" ? "SMS to clients is off until 10DLC is approved" : errorMessage(e));

/** Text sheet (SPEC §6.1 item 4): quick replies as chips above the message; POST /api/messages/sms. */
export function TextSheet({ target, onClose }: { target: CommsTarget; onClose: () => void }) {
  const toast = useToast();
  const quick = useQuery(quickRepliesQuery(target.leadId));
  const [body, setBody] = useState("");
  const [deliveryError, setDeliveryError] = useState<string | null>(null);
  const policy = contactPolicy(target, "sms");
  const input = { leadId: target.leadId, contactId: target.contactId, body };
  const parsed = parseInput(smsSend, input, { type: "message" }, "comms.send_sms");
  const validation = useFormValidation(manualMessageFindings("sms", input, target));
  const { busy } = validation;
  const edit = (value: string) => validation.edit(() => { setBody(value); setDeliveryError(null); });

  async function send() {
    if (!policy.allowed) return toast.error(policy.reason);
    if (!parsed.success || !validation.begin("comms.send_sms")) return;
    setDeliveryError(null);
    try {
      const r = await leadApi.sendSms(parsed.data);
      validation.clear();
      const feedback = manualSendFeedback("sms", r.status, r.error);
      if (!feedback.accepted) { setDeliveryError(feedback.message); return; }
      toast.show(feedback.message);
      const w = warningMessage(r.warning);
      if (w) toast.show(w);
      onClose();
    } catch (e) {
      validation.reject(e);
      setDeliveryError(validationUnavailable(e) ? uncertainSendMessage : smsError(e));
    } finally {
      validation.finish();
    }
  }

  return (
    <BottomSheet
      open
      onClose={() => validation.edit(onClose)}
      title={`Text ${target.name}`}
      footer={
        <Button loading={busy} disabled={!policy.allowed} onClick={send}>
          Send text
        </Button>
      }
    >
      <Stack gap={3}>
        <UnsavedChanges dirty={Boolean(body)} pending={busy} />
        <ValidationSummary result={validation.result} local unavailable={validation.unavailable} context="Draft feedback. Recipient and delivery checks run again when you send." />
        {deliveryError && <div role="alert"><Text variant="callout" tone="danger">{deliveryError}</Text></div>}
        {!policy.allowed && (
          <Text variant="callout" tone="danger">
            {policy.reason}
          </Text>
        )}
        {quick.isLoading ? (
          <Skeleton count={1} />
        ) : quick.data && quick.data.length > 0 ? (
          <ChipStrip label="Quick replies">
            {quick.data.map((q) => (
              <SelectChip key={q.id} selected={body === q.body} disabled={busy || !policy.allowed} onClick={() => edit(q.body)}>
                {q.name}
              </SelectChip>
            ))}
          </ChipStrip>
        ) : null}
        <TextArea label={`To ${fmtPhone(target.phone) || "—"}`} placeholder="Type a message" value={body} {...validation.field("body")} disabled={busy || !policy.allowed} onChange={(e) => edit(e.target.value)} autoFocus />
      </Stack>
    </BottomSheet>
  );
}

const OUTCOMES = [
  { key: "no_answer", label: "No answer" },
  { key: "voicemail", label: "Voicemail" },
  { key: "spoke", label: "Spoke" },
] as const;

/** Log call sheet: a tel: link on top, outcome + notes → POST /api/messages/call (counts as outbound activity). */
export function CallSheet({ target, onClose }: { target: CommsTarget; onClose: () => void }) {
  const toast = useToast();
  const [outcome, setOutcome] = useState<(typeof OUTCOMES)[number]["key"]>("no_answer");
  const [notes, setNotes] = useState("");
  const policy = contactPolicy(target, "call");
  const parsed = parseInput(callLog, { leadId: target.leadId, contactId: target.contactId, outcome, notes: notes.trim() || undefined }, { type: "message" }, "message.save");
  const validation = useFormValidation(evaluation(parsed.issues, false));
  const { busy } = validation;
  const [failure, setFailure] = useState<string | null>(null);

  async function log() {
    if (!parsed.success || !validation.begin("message.save")) return;
    setFailure(null);
    try {
      await leadApi.logCall(parsed.data);
      toast.success("Call logged");
      onClose();
    } catch (e) {
      validation.reject(e); setFailure(errorMessage(e));
    } finally {
      validation.finish();
    }
  }

  return (
    <BottomSheet
      open
      onClose={() => validation.edit(onClose)}
      title={`Call ${target.name}`}
      footer={
        <Button loading={busy} onClick={log}>
          Log call
        </Button>
      }
    >
      <Stack gap={4}>
        <UnsavedChanges dirty={Boolean(notes) || outcome !== "no_answer"} pending={busy} />
        <ValidationSummary result={validation.result} local unavailable={validation.unavailable} />
        {failure && <div role="alert"><Text variant="callout" tone="danger">{failure}</Text></div>}
        <Button variant="secondary" href={policy.allowed ? `tel:${target.phone}` : undefined} disabled={!policy.allowed} icon={<Phone aria-hidden size={18} />}>
          {target.phone ? `Call ${fmtPhone(target.phone)}` : "No phone number"}
        </Button>
        {!policy.allowed && <Text variant="callout" tone="danger">{policy.reason} You can still log a previous call.</Text>}
        <SegmentedControl label="Outcome" items={OUTCOMES} value={outcome} disabled={busy} onChange={value => validation.edit(() => { setOutcome(value); setFailure(null); })} />
        <TextArea label="Notes" placeholder="What was said, what's next" value={notes} {...validation.field("notes")} onChange={(e) => validation.edit(() => { setNotes(e.target.value); setFailure(null); })} />
      </Stack>
    </BottomSheet>
  );
}

/** Email sheet: subject + body → POST /api/messages/email. */
export function EmailSheet({ target, onClose }: { target: CommsTarget; onClose: () => void }) {
  const toast = useToast();
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [deliveryError, setDeliveryError] = useState<string | null>(null);
  const policy = contactPolicy(target, "email");
  const input = { leadId: target.leadId, contactId: target.contactId, subject, body };
  const parsed = parseInput(emailSend, input, { type: "message" }, "comms.send_email");
  const validation = useFormValidation(manualMessageFindings("email", input, target));
  const { busy } = validation;
  const edit = (change: () => void) => validation.edit(() => { change(); setDeliveryError(null); });

  async function send() {
    if (!policy.allowed) return toast.error(policy.reason);
    if (!parsed.success || !validation.begin("comms.send_email")) return;
    setDeliveryError(null);
    try {
      const r = await leadApi.sendEmail(parsed.data);
      validation.clear();
      const feedback = manualSendFeedback("email", r.status, r.error);
      if (!feedback.accepted) { setDeliveryError(feedback.message); return; }
      toast.show(feedback.message);
      onClose();
    } catch (e) {
      validation.reject(e);
      setDeliveryError(validationUnavailable(e) ? uncertainSendMessage : errorMessage(e));
    } finally {
      validation.finish();
    }
  }

  return (
    <BottomSheet
      open
      onClose={() => validation.edit(onClose)}
      title={`Email ${target.name}`}
      footer={
        <Button loading={busy} disabled={!policy.allowed} onClick={send}>
          Send email
        </Button>
      }
    >
      <Stack gap={3}>
        <UnsavedChanges dirty={Boolean(subject || body)} pending={busy} />
        <ValidationSummary result={validation.result} local unavailable={validation.unavailable} context="Draft feedback. Recipient and delivery checks run again when you send." />
        {deliveryError && <div role="alert"><Text variant="callout" tone="danger">{deliveryError}</Text></div>}
        {!policy.allowed && (
          <Text variant="callout" tone="danger">
            {policy.reason}
          </Text>
        )}
        <TextField label={`To ${target.email ?? "—"}`} placeholder="Subject" value={subject} {...validation.field("subject")} disabled={busy || !policy.allowed} onChange={(e) => edit(() => setSubject(e.target.value))} autoFocus />
        <TextArea label="Message" value={body} {...validation.field("body")} disabled={busy || !policy.allowed} onChange={(e) => edit(() => setBody(e.target.value))} />
      </Stack>
    </BottomSheet>
  );
}
